import React from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import type { PluggableList } from 'unified';
import CodeBlock from './CodeBlock';

// Module-level stable references to prevent ReactMarkdown re-parsing on every render.
// remarkBreaks renders single newlines as <br> so signatures, addresses, and other
// intentional line breaks in AI responses display as written (CommonMark would
// otherwise collapse a single newline into a space).
const remarkPlugins = [remarkGfm, remarkBreaks];
const rehypePlugins: PluggableList = [rehypeSanitize, [rehypeHighlight, { plainText: ['spreadsheet', 'document', 'presentation', 'infographic', 'chart'] }]];

const markdownComponents = {
  code({ className, children, ...rest }: ComponentPropsWithoutRef<'code'>) {
    const isBlock = /language-/.test(className ?? '');
    if (isBlock) {
      return <CodeBlock className={className}>{children}</CodeBlock>;
    }
    return <code className={className} {...rest}>{children}</code>;
  },
  pre({ children }: ComponentPropsWithoutRef<'pre'>) {
    return <>{children}</>;
  },
};

interface MarkdownRendererProps {
  content: string;
}

function MarkdownRenderer({ content }: MarkdownRendererProps) {
  return (
    <div className="markdown-content text-sm">
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default React.memo(MarkdownRenderer);
